import { APP_BRAND_NAME } from './brand'
import { api_fetch } from './api'
import { store } from './store'

const DEFAULT_HOSTS = new Set([
  'fidelidade.sistemaaguia.com',
  'fidelidade-dev.sistemaaguia.com',
  'nabomba.com',
  'www.nabomba.com',
  'nabomba.com.br',
  'www.nabomba.com.br',
  'v2.nabomba.com',
  'dev.nabomba.com',
  'dev.nabomba.com.br',
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '[::1]',
])

export type RedeContext =
  | { mode: 'default'; host: string }
  | {
      mode: 'rede'
      host: string
      uuid_rede: string
      nome_rede: string
      logotipo: string
      telefone: string
      email: string
    }
  | { mode: 'unconfigured'; host: string }

function text(value: unknown): string {
  return (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').trim()
}

function current_host(): string {
  return text(window.location.hostname).toLowerCase()
}

function is_default_host(host: string): boolean {
  if (!host) return true
  if (DEFAULT_HOSTS.has(host)) return true
  if (host.endsWith('.cloudfront.net')) return true
  if (host.endsWith('.elb.amazonaws.com')) return true
  return false
}

function fallback_context(): RedeContext {
  const host = current_host()
  return is_default_host(host)
    ? { mode: 'default', host }
    : { mode: 'unconfigured', host }
}

function is_rede_context(value: unknown): value is RedeContext {
  if (!value || typeof value !== 'object') return false
  const mode = (value as { mode?: unknown }).mode
  return mode === 'default' || mode === 'rede' || mode === 'unconfigured'
}

function apply_rede_context(context: RedeContext): void {
  store.set('rede_mode', context.mode)
  store.set('rede_host', text(context.host) || null)

  if (context.mode !== 'rede') {
    store.set('rede_uuid', null)
    store.set('rede_nome', null)
    store.set('rede_logo', null)
    store.set('rede_telefone', null)
    store.set('rede_email', null)
    return
  }

  store.set('rede_uuid', text(context.uuid_rede) || null)
  store.set('rede_nome', text(context.nome_rede) || null)
  store.set('rede_logo', text(context.logotipo) || null)
  store.set('rede_telefone', text(context.telefone) || null)
  store.set('rede_email', text(context.email) || null)
}

export async function sync_rede_context(): Promise<RedeContext> {
  try {
    const res = await api_fetch('/api/rede/context', { credentials: 'same-origin' }, 8000)
    const body = await res.json().catch(() => null) as RedeContext | null
    const context = is_rede_context(body) ? body : fallback_context()
    apply_rede_context(context)
    return context
  } catch {
    const fallback = fallback_context()
    apply_rede_context(fallback)
    return fallback
  }
}

export function is_rede_mode(): boolean {
  return store.get('rede_mode') === 'rede'
}

export function is_rede_unconfigured(): boolean {
  return store.get('rede_mode') === 'unconfigured'
}

export function rede_display_name(): string {
  const name = text(store.get('rede_nome'))
  if (name) return name
  if (is_rede_mode()) return text(store.get('rede_host')) || 'Portal Fidelidade'
  return APP_BRAND_NAME
}

export function rede_share_copy(): string {
  return is_rede_mode()
    ? `Confira os preços fidelidade da ${rede_display_name()}`
    : 'Confira os preços fidelidade na nabomba'
}

export const DEFAULT_CONTACT_EMAIL = 'faleconosco@nabomba.com.br'

export function contact_email(): string {
  return is_rede_mode() ? text(store.get('rede_email')) || DEFAULT_CONTACT_EMAIL : DEFAULT_CONTACT_EMAIL
}
